import { action, observable, runInAction } from 'mobx';
import { priceListController } from './priceListController';
import {
  GetPriceListItemRequest,
  EditPriceListItemRequest,
  CreatePriceListItemRequest,
  DeletePriceListItemRequest,
  CalculatePriceRequest,
  CalculatePriceResponse,
  CalculatePriceStorageRequest,
  CalculatePriceStorageResponse,
  PriceListItemCalculateComplexTableInterface,
} from '../api/priceListApi';

// Полная позиция со всем строками
export interface PriceListItem {
  id: string,
  positionId?: string,
  positionName?: string,
  positionShortName?: string,
  classifier1RefValueId?: number,
  classifier1RefValueName?: string,
  classifier2RefValueId?: number,
  classifier2RefValueName?: string,
  price?: number,
  // цена комплекса (для расчёта комплексов ШМ), не зависит от обычной цены
  complexPrice?: number,
  value? : string,
  updated?: boolean,
  // черновик ввода комплексной цены и признак правки — только для UI,
  // как value/updated у обычной цены; отдельные поля, чтобы правки обычной
  // и комплексной цены одной позиции не влияли друг на друга
  complexValue?: string,
  complexUpdated?: boolean,
  markedToDelete?: boolean,
  selected?: boolean,
  positionVendorCode?: string,
  backgroundColor?: string,
  calculatorClassifierRefValueId?: number,
  calculatorClassifierRefValueName?: string,
  calculatorClassifierRefValueSort?: number,
  positionDeletedDate?: string,
  runFlatDependent?: boolean,
  // далее позиции для управления интерфейсом
  countToAdd?: number, // количество для добавления в счёт
  dividerName?: string,
}

export interface PriceListItemSelected extends PriceListItem {
  amount?: number,
}

export interface PriceListItemsStore {
  priceListItems: PriceListItem[],
  priceListId: string, // данные по этому прайсу находят в priceListItems
  error: any,
  currentPriceListItem: PriceListItem,

  selectedPriceListItems: PriceListItemSelected[],
  selectedPriceListItemsByOrderId: string,

  calculateComplexTableList: PriceListItemCalculateComplexTableInterface[],
  calculateComplexTableListBypriceListId: string,
}

export const priceListItemsStore: PriceListItemsStore = observable({
  priceListItems: [],
  priceListId: null,
  error: null,
  currentPriceListItem: null,
  selectedPriceListItems: [],
  selectedPriceListItemsByOrderId: null,

  calculateComplexTableList: [],
  calculateComplexTableListBypriceListId: null,
} as PriceListItemsStore);

export const priceListItemsActions = {
  getPriceListItems: async (request: GetPriceListItemRequest): Promise<void> => {
    const response = await priceListController.getPriceListItems(request);
    priceListItemsActions.handleError(response);
    runInAction(() => {
      priceListItemsStore.priceListId = request.priceListId;
      priceListItemsStore.priceListItems = response.data;
    });
  },

  clearPriceListItems: async (): Promise<void> => {
    runInAction(() => {
      priceListItemsStore.priceListId = null;
      priceListItemsStore.priceListItems = [];
    });
  },

  getPriceListItemFromList: async (id: string): Promise<void> => {
    const response = priceListItemsStore.priceListItems.find((v) => v.id === id);
    priceListItemsActions.setCurrentPriceListItem(response || null);
  },

  addAllPriceListItems: async (id: string): Promise<void> => {
    const response = await priceListController.addAllPriceListItems(id);
    priceListItemsActions.handleError(response);
  },

  addAllItemsToExistPositions: async (id: string): Promise<void> => {
    const response = await priceListController.addAllItemsToExistPositions(id);
    priceListItemsActions.handleError(response);
  },

  editPriceListItem: async (id: string, request: EditPriceListItemRequest): Promise<void> => {
    const response = await priceListController.editPriceListItem(id, request);
    priceListItemsActions.handleError(response);
    // priceListItemsActions.setCurrentPriceListItem(response.data);
  },

  createPriceListItem: async (request: CreatePriceListItemRequest): Promise<void> => {
    const response = await priceListController.createPriceListItem(request);
    priceListItemsActions.handleError(response);
  },

  putPriceListItem: async (request: CreatePriceListItemRequest): Promise<void> => {
    const response = await priceListController.putPriceListItem(request);
    priceListItemsActions.handleError(response);
  },

  deletePriceListItem: async (id: string): Promise<void> => {
    const response = await priceListController.deletePriceListItem(id);
    priceListItemsActions.handleError(response);
  },

  deletePriceListItemPosition: async (request: DeletePriceListItemRequest): Promise<void> => {
    const response = await priceListController.deletePriceListItemPosition(request);
    priceListItemsActions.handleError(response);
  },

  clearCachePriceInRedis: async (): Promise<void> => {
    const response = await priceListController.clearCachePriceInRedis();
    priceListItemsActions.handleError(response);
  },

  clearCurrentItem: action(() => {
    priceListItemsStore.currentPriceListItem = null;
  }),

  clearError: action(() => {
    priceListItemsStore.error = null;
  }),

  handleError: action((response) => {
    if (response.errorData) {
      const { errorData } = response;
      priceListItemsStore.error = errorData;
      throw errorData;
    }
  }),

  setCurrentPriceListItem: action((priceListItem: PriceListItem) => {
    priceListItemsStore.currentPriceListItem = priceListItem;
  }),

  setPriceListItemsToStore: action((newPriceListItems: PriceListItem[]) => {
    runInAction(() => {
      priceListItemsStore.priceListItems = newPriceListItems;
    });
    // priceListItemsStore.priceListItems[priceListItemsStore.priceListItems.indexOf(v => v.id === id)].basePrice = price;
  }),

  setUpdatedItemToStore: action((newPriceListItem: PriceListItem) => {
    const updatedItems = [...priceListItemsStore.priceListItems];
    const itemIndex = updatedItems.findIndex(k => k.id === newPriceListItem.id);
    updatedItems[itemIndex] = newPriceListItem;
    priceListItemsActions.setPriceListItemsToStore(updatedItems);
  }),

  // Точечное слияние полей нескольких айтемов с ТЕКУЩИМ массивом стора.
  // В отличие от setPriceListItemsToStore(готовый массив) не может затереть
  // черновики соседних ячеек устаревшим снимком: блюр-сохранение ждёт ответа
  // API, а пользователь уже вводит цену в следующей ячейке после Tab
  patchPriceListItems: action((ids: string[], patch: Partial<PriceListItem>) => {
    const idSet = new Set(ids);
    priceListItemsStore.priceListItems = priceListItemsStore.priceListItems
      .map((v) => (idSet.has(v.id) ? { ...v, ...patch } : v));
  }),

  getPrice: async (request: CalculatePriceRequest): Promise<CalculatePriceResponse> => {
    const response = await priceListController.getPrice(request);
    return response;
  },

  getStoragePrice: async (request: CalculatePriceStorageRequest): Promise<CalculatePriceStorageResponse> => {
    const response = await priceListController.getStoragePrice(request);
    return response;
  },

  // Просмотр прайса офиса (кнопка «Посмотреть прайс» в шапке): как getPrice/getStoragePrice,
  // возвращают ответ вызывающему и не пишут в общие поля стора — кнопка доступна с любой
  // страницы, включая открытые страницы редактирования прайсов, данные которых затирать нельзя
  getPriceListItemsData: async (request: GetPriceListItemRequest): Promise<any> => {
    const response = await priceListController.getPriceListItems(request);
    return response;
  },

  getCalculateComplexTableListData: async (priceListId: string): Promise<any> => {
    const response = await priceListController.getCalculateComplexTableList(priceListId);
    return response;
  },

  // цены комплексов для одной комбинации тип ТС × диаметр (Б8): как ...Data-методы выше,
  // возвращает ответ вызывающему и не пишет в общие поля стора — мастер кеширует по комбинации сам
  getComplexPricesData: async (
    priceListId: string,
    vehicleTypeRefValueId: number,
    diameterRefValueId: number
  ): Promise<any> => {
    const response = await priceListController.getComplexPrices(
      priceListId, vehicleTypeRefValueId, diameterRefValueId
    );
    return response;
  },

  // блок управление выбранными позициями в заявке
  addItemToSelectedPriceListItems: action((request: PriceListItem) => {
    const x = priceListItemsStore.selectedPriceListItems.find((v) => v.id === request.id);
    if (x) {
      priceListItemsStore.selectedPriceListItems = priceListItemsStore.selectedPriceListItems.map((v) => {
        const a:number = typeof v.amount === 'string' ? parseInt(v.amount, 10) ?? 0 : v.amount ?? 0;
        if (v.id === request.id) return { ...v, amount: (Number.isNaN(a) ? 0 : a) + 1 };
        return v;
      });
      return;
    }

    priceListItemsStore.selectedPriceListItems = [...priceListItemsStore.selectedPriceListItems,
      { ...request, amount: 1 }];
  }),

  updateItemToSelectedPriceListItems: action((id:string, amount: number) => {
    const a:number = (typeof (amount) !== 'undefined' && amount < 0) ? 1 : amount;
    priceListItemsStore.selectedPriceListItems = priceListItemsStore.selectedPriceListItems.map((v) => {
      if (v.id === id) return { ...v, amount: a };
      return v;
    });
  }),

  deleteItemToSelectedPriceListItems: action((request: PriceListItem) => {
    const x = priceListItemsStore.selectedPriceListItems.find((v) => v.id === request.id);

    if ((x && typeof x.amount === 'string' && x.amount === '') || (x && x.amount === 1)) {
      priceListItemsStore.selectedPriceListItems = priceListItemsStore.selectedPriceListItems
        .filter((v) => v.id !== request.id);

      return;
    }

    if (x && x.amount > 1) {
      priceListItemsStore.selectedPriceListItems = priceListItemsStore.selectedPriceListItems.map((v) => {
        if (v.id === request.id) return { ...v, amount: x.amount - 1 };
        return v;
      });
    }
  }),

  clearSelectedPriceListItems: action(() => {
    priceListItemsStore.selectedPriceListItems = [];
  }),

  setSelectedPriceListItemsByOrderId: action((id:string) => {
    priceListItemsStore.selectedPriceListItemsByOrderId = id;
  }),

  // блок управление выбранными позициями в заявке

  getCalculateComplexTableList: async (priceListId : string): Promise<void> => {
    runInAction(() => {
      priceListItemsStore.calculateComplexTableListBypriceListId = priceListId;
    });

    const response = await priceListController.getCalculateComplexTableList(priceListId);
    priceListItemsActions.handleError(response);
    runInAction(() => {
      priceListItemsStore.calculateComplexTableList = response.data.items;
    });
  },

  clearCalculateComplexTableList: action(() => {
    priceListItemsStore.calculateComplexTableList = [];
    priceListItemsStore.calculateComplexTableListBypriceListId = null;
  }),

};
